import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar, Building2, Video, Copy, Download, Plus, Check, ChevronUp, ChevronDown, AlertOctagon, FileText, ExternalLink, QrCode, Settings, HelpCircle, X, BarChart3 } from 'lucide-react';
import { fetchProgramBySO, fetchSessions, fetchAttendees, addAttendee, updateAttendanceStatus, createAttendanceRecord, updateSONotes } from '../lib/airtable';
import typeformService from '../lib/typeform';
import { useToast } from '@/hooks/use-toast';
import { useConditionalClient } from '../hooks/useConditionalClient';
import { validateClientSelection } from '../lib/attendee-utils';
import { RESERVED_ROUTES } from '../config/routes';
import { analytics } from '../lib/analytics';
import { Spinner } from './ui/spinner';
import { ProgramSkeleton } from './ui/ProgramSkeleton';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { QRCodeSVG } from 'qrcode.react';
import SessionNotesModal from './SessionNotesModal';
import SOSearchDropdown from './SOSearchDropdown';
import { useAirtableOutage } from '../hooks/useAirtableOutage';
import MaterialsSection from './MaterialsSection';

// Normalized type sets for reliable matching
const VIRTUAL_TYPES = new Set(['virtual', 'pilot-virtual', 'open/trial']);
const IN_PERSON_TYPES = new Set(['in-person']);
const PILOT_IN_PERSON_TYPES = new Set(['pilot-in-person']);

// Helper function to normalize type input
const normalizeType = (type) => {
  if (typeof type !== 'string') return '';
  return type.trim().toLowerCase();
};

// Utility functions for type checking
const isVirtualType = (type) => {
  return VIRTUAL_TYPES.has(normalizeType(type));
};

const isInPersonType = (type) => {
  return IN_PERSON_TYPES.has(normalizeType(type));
};

const isPilotInPersonType = (type) => {
  return PILOT_IN_PERSON_TYPES.has(normalizeType(type));
};

// Determine if a session should be virtual based on program type
const isSessionVirtual = (programType) => {
  // Pilot-Virtual: all sessions virtual (like Virtual)
  if (isVirtualType(programType)) {
    return true;
  }
  
  // Pilot-In-Person: ALL sessions in-person (no exceptions)
  if (isPilotInPersonType(programType)) {
    return false;
  }
  
  // Regular In-Person: ALL sessions are in-person (like Pilot-In-Person)
  if (isInPersonType(programType)) {
    return false;
  }
  
  return false;
};

// Helper function to identify Connected Leader programs
const isConnectedLeaderProgram = (programName) => {
  if (!programName) return false;
  
  // Handle array values from Airtable lookup fields
  const nameToCheck = Array.isArray(programName) ? programName[0] : programName;
  if (!nameToCheck) return false;
  
  // Check for various Connected Leader program name patterns
  const normalizedName = nameToCheck.toLowerCase().trim();
  return normalizedName.includes('connected leader') || 
         normalizedName.includes('tcl') ||
         normalizedName === 'the connected leader';
};

// Helper function to determine if survey should be shown
const shouldShowSurvey = (programName, sessionIndex, totalSessions, hasSurveyLink) => {
  // If there's no survey link, don't show survey
  if (!hasSurveyLink) return false;
  
  // For Connected Leader programs, only show survey on final session
  if (isConnectedLeaderProgram(programName)) {
    const isLastSession = sessionIndex === totalSessions - 1;
    return isLastSession;
  }
  
  // For all other programs, show survey if survey link exists
  return true;
};

const formatDate = (dateString, forCSV = false) => {
  // Check if the date string is just a date (YYYY-MM-DD) without time
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  
  let date;
  if (isDateOnly) {
    // For date-only strings, parse as local date to avoid timezone shifts
    const [year, month, day] = dateString.split('-');
    date = new Date(year, month - 1, day);
  } else {
    // For date-time strings, use normal parsing
    date = new Date(dateString);
  }
  
  if (forCSV) {
    // For CSV files: use a format without commas
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date).replace(/,/g, '');
  } else {
    // For display: use regular format with commas
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  }
};

const ProgramManager = ({ readOnly = false, hideNavButtons = false, salesMode = false, adminMode = false }) => {
  const { soNumber } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchSO, setSearchSO] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingAttendees, setLoadingAttendees] = useState(false);
  const [programData, setProgramData] = useState(null);
  const [error, setError] = useState('');
  const isAirtableDown = useAirtableOutage();

  // Remove the old copy state variables since we're not using them anymore
  // const [copyingSurveyLink, setCopyingSurveyLink] = useState(false);
  // const [copyingPageLink, setCopyingPageLink] = useState(false);
  
  // Simple boolean states plus timestamps to trigger useEffect
  const [pageCopied, setPageCopied] = useState(false);
  const [pageCopyTime, setPageCopyTime] = useState(null);
  
  // Session survey copy state
  const [copiedSessionSurveys, setCopiedSessionSurveys] = useState({});
  
  const [attendees, setAttendees] = useState([]);
  const [newAttendee, setNewAttendee] = useState({ firstName: '', lastName: '', email: '', clientId: '' });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [loadingAttendanceMap, setLoadingAttendanceMap] = useState({});
  const [addingAttendee, setAddingAttendee] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sortedAttendees, setSortedAttendees] = useState([]);
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortField, setSortField] = useState('name'); // 'name' or 'company'
  const [clientFilter, setClientFilter] = useState('all');
  const [uniqueClients, setUniqueClients] = useState([]);
  const { toast } = useToast();
  const [readOnlyMode, setReadOnlyMode] = useState(readOnly);

  // Conditional client management hook - pass the full SO record
  const conditionalClient = useConditionalClient(programData?.soRecord);

  // Notes modal state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  
  // QR code modal state
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrSurveyUrl, setQrSurveyUrl] = useState('');
  const [selectedNotesData, setSelectedNotesData] = useState(null);
  
  // Duplicate attendee check state
  const [duplicateCheckOpen, setDuplicateCheckOpen] = useState(false);
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);
  
  // SO Notes state
  const [soNotes, setSoNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  
  // Typeform response counts state
  const [responseCounts, setResponseCounts] = useState({});
  const [loadingResponseCounts, setLoadingResponseCounts] = useState({});
  
  // Instructions visibility state (default to closed)
  const [showInstructions, setShowInstructions] = useState(false);
  const [showMaterialsInstructions, setShowMaterialsInstructions] = useState(false);

  useEffect(() => {
    // Reserved routes that should not be treated as SO numbers
    
    // Check URL parameter (route parameter takes precedence)
    const soFromUrl = soNumber || searchParams.get('so');
    
    // Skip if it's a reserved route
    if (soFromUrl && !RESERVED_ROUTES.includes(soFromUrl.toLowerCase())) {
      // Always update the search field to match the URL
      setSearchSO(soFromUrl);
      // Fetch data for the SO in the URL
      fetchProgramData(soFromUrl);
    }
  }, [soNumber, searchParams]); // fetchProgramData has empty deps and is defined later

  // Check environment variables on component mount
  useEffect(() => {
    const apiKey = import.meta.env.VITE_AIRTABLE_ACCESS_TOKEN;
    const baseId = import.meta.env.VITE_AIRTABLE_OPERATIONS_BASE_ID;
    
    if (!apiKey) {
      console.error('Airtable API token is missing. Please check your .env file.');
      // Only show error toasts for critical issues
      toast({
        title: "Error",
        description: 'API token is missing. Please contact the administrator.',
        variant: "destructive",
      });
    } else if (!baseId) {
      console.error('Airtable Operations Base ID is missing. Please check your .env file.');
      // Only show error toasts for critical issues
      toast({
        title: "Error",
        description: 'Base ID is missing. Please contact the administrator.',
        variant: "destructive",
      });
    }
  }, [toast]);

  // Pre-populate client for non-open programs using useConditionalClient hook
  useEffect(() => {
    if (conditionalClient.programType === 'regular' && conditionalClient.selectedClient) {
      // Auto-assign client for regular programs
      if (newAttendee.clientId === '') {
        setNewAttendee(prev => ({
          ...prev,
          clientId: conditionalClient.selectedClient.id
        }));
      }
    }
  }, [conditionalClient.programType, conditionalClient.selectedClient, newAttendee.clientId]);

  // Helper function to get default client ID for new attendees
  const getDefaultClientId = useCallback(() => {
    if (!programData) return '';

    const isOpenProgram = conditionalClient.programType === 'open';
    if (isOpenProgram) return ''; // No default for open programs

    // Return auto-assigned client for regular programs
    if (conditionalClient.selectedClient) {
      return conditionalClient.selectedClient.id;
    }

    return '';
  }, [programData, conditionalClient.programType, conditionalClient.selectedClient]);

  // Helper function to determine if we should show client column
  const shouldShowClientColumn = useCallback(() => {
    // Always show client column - users want to see client names even with single client
    return true;
  }, []);

  const filterAndSortAttendees = useCallback(() => {
    // First filter by client
    let filtered = attendees;
    if (clientFilter !== 'all') {
      filtered = attendees.filter(attendee => attendee.clientName === clientFilter);
    }
    
    // Then sort by selected field with secondary sorting
    const sorted = [...filtered].sort((a, b) => {
      let primaryA, primaryB, secondaryA, secondaryB;
      
      if (sortField === 'company') {
        // Primary sort by company, secondary by name
        primaryA = (a.clientName || '').toLowerCase();
        primaryB = (b.clientName || '').toLowerCase();
        secondaryA = `${a.firstName} ${a.lastName}`.toLowerCase();
        secondaryB = `${b.firstName} ${b.lastName}`.toLowerCase();
      } else { // sortField === 'name'
        // Primary sort by name, secondary by company
        primaryA = `${a.firstName} ${a.lastName}`.toLowerCase();
        primaryB = `${b.firstName} ${b.lastName}`.toLowerCase();
        secondaryA = (a.clientName || '').toLowerCase();
        secondaryB = (b.clientName || '').toLowerCase();
      }
      
      // Compare primary field first
      const primaryComparison = sortDirection === 'asc' 
        ? primaryA.localeCompare(primaryB)
        : primaryB.localeCompare(primaryA);
      
      // If primary fields are equal, use secondary field (always ascending)
      if (primaryComparison === 0) {
        return secondaryA.localeCompare(secondaryB);
      }
      
      return primaryComparison;
    });
    
    setSortedAttendees(sorted);
  }, [attendees, clientFilter, sortDirection, sortField]);

  // Update unique clients when attendees change
  useEffect(() => {
    if (attendees.length) {
      const clients = [...new Set(
        attendees
          .map(attendee => attendee.clientName)
          .filter(Boolean)
      )].sort();
      
      setUniqueClients(clients);
      filterAndSortAttendees();
    } else {
      setUniqueClients([]);
      setSortedAttendees([]);
    }
  }, [attendees, filterAndSortAttendees]);

  // Re-filter and sort when client filter changes
  useEffect(() => {
    filterAndSortAttendees();
  }, [clientFilter, filterAndSortAttendees]);

  // Toggle sort for any column
  const toggleSort = (field) => {
    if (sortField === field) {
      // If already sorting by this field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If sorting by different field, switch to this field and set ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };


  // Simple page link copy function
  const handleCopyPageLink = () => {
    // If we have program data, use the SO number from it; otherwise use current URL
    const currentSO = programData?.so || soNumber;
    const url = currentSO 
      ? `${window.location.origin}/${currentSO}`
      : `${window.location.origin}${window.location.pathname}`;
    
    navigator.clipboard.writeText(url)
      .then(() => {
        setPageCopied(true);
        setPageCopyTime(Date.now());
        toast({
          title: "Copied!",
          description: "Page link copied to clipboard.",
          variant: "success",
        });
      })
      .catch(() => {
        toast({
          title: "Error",
          description: "Failed to copy page link",
          variant: "destructive",
        });
      });
  };

  // Handle session survey copying
  const handleSessionSurveyCopy = (sessionId, surveyLink) => {
    navigator.clipboard.writeText(surveyLink)
      .then(() => {
        setCopiedSessionSurveys(prev => ({
          ...prev,
          [sessionId]: true
        }));
        
        // Reset after 3 seconds
        setTimeout(() => {
          setCopiedSessionSurveys(prev => {
            const updated = {...prev};
            delete updated[sessionId];
            return updated;
          });
        }, 3000);
        
        toast({
          title: "Copied!",
          description: "Survey link copied to clipboard.",
          variant: "success",
        });
      })
      .catch(() => {
        toast({
          title: "Error",
          description: "Failed to copy survey link",
          variant: "destructive",
        });
      });
  };

  // Handle QR code modal opening
  const handleShowQRCode = (surveyUrl) => {
    setQrSurveyUrl(surveyUrl);
    setQrModalOpen(true);
  };

  // Handle QR code download
  const handleDownloadQR = () => {
    const svg = document.querySelector('.qr-code-svg');
    if (svg) {
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      canvas.width = 400;
      canvas.height = 400;
      
      img.onload = () => {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `survey-qr-code-${searchSO || 'survey'}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          
          toast({
            title: "Downloaded!",
            description: "QR code image saved to your downloads.",
            variant: "success",
          });
        });
      };
      
      img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
    }
  };

  // Effect to reset page copy state
  useEffect(() => {
    if (pageCopyTime) {
      const timer = setTimeout(() => {
        setPageCopied(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [pageCopyTime]);

  const fetchProgramData = useCallback(async (soNumber) => {
    const startTime = performance.now();
    setLoading(true);
    setLoadingSessions(false);
    setLoadingAttendees(false);
    setError('');
    setProgramData(null);
    setAttendees([]);
    setSoNotes('');
    
    try {
      // Step 1: Fetch basic program data first
      const program = await fetchProgramBySO(soNumber);
      
      // Track successful search
      analytics.searchSO(soNumber, true, Math.round(performance.now() - startTime));
      
      // Show program info immediately (without sessions)
      setProgramData(program);
      setSoNotes(program.soNotes || '');
      setLoading(false); // Main loading done, show the UI

      // Step 2: Set individual loading states
      setLoadingSessions(true);
      setLoadingAttendees(true);
      
      // Fetch sessions and attendees in parallel
      const sessionsStartTime = performance.now();
      const sessionsPromise = fetchSessions(program.so).then(async sessions => {
        setProgramData(prev => ({ ...prev, sessions }));
        setLoadingSessions(false);
        
        // Track sessions loading
        analytics.loadSessions(sessions.length, Math.round(performance.now() - sessionsStartTime));
        
        // Fetch Typeform response counts for sessions with survey links
        const counts = {};
        
        // Efficiently initialize loading states for sessions with survey links
        const loadingStates = sessions.reduce((acc, session) => {
          if (session.surveyLink) acc[session.sessionId] = true;
          return acc;
        }, {});
        setLoadingResponseCounts(loadingStates);
        
        for (const session of sessions) {
          if (session.surveyLink) {
            const formId = typeformService.extractFormIdFromUrl(session.surveyLink);
            if (formId) {
              try {
                const responseCount = await typeformService.getResponseCount(formId);
                counts[session.sessionId] = {
                  responses: responseCount,
                  attendees: 0 // Will be updated after attendees are loaded
                };
              } catch (error) {
                console.error(`Failed to fetch response count for session ${session.sessionId}:`, error);
                counts[session.sessionId] = {
                  responses: 0,
                  attendees: 0
                };
              }
              // Update count for this session immediately to avoid UI flicker
              setResponseCounts(prev => ({
                ...prev,
                [session.sessionId]: counts[session.sessionId] || { responses: 0, attendees: 0 }
              }));
            } else {
              // If formId extraction failed, set default counts
              counts[session.sessionId] = {
                responses: 0,
                attendees: 0
              };
              setResponseCounts(prev => ({
                ...prev,
                [session.sessionId]: counts[session.sessionId]
              }));
            }
            // Always clear loading state for this session, regardless of formId success
            setLoadingResponseCounts(prev => ({
              ...prev,
              [session.sessionId]: false
            }));
          }
        }
        // No need to set all counts at once since we're updating incrementally
        
        return sessions;
      });
      
      const attendeesStartTime = performance.now();
      const attendeesPromise = fetchAttendees(program.soid, program.so).then(attendeesList => {
        setAttendees(attendeesList);
        setLoadingAttendees(false);
        
        // Track attendees loading
        analytics.loadAttendees(attendeesList.length, Math.round(performance.now() - attendeesStartTime));
        
        // Update response counts with attendee counts per session
        updateAttendeeCountsInResponses(attendeesList);
        
        return attendeesList;
      });
      
      // Wait for both to complete
      await Promise.all([sessionsPromise, attendeesPromise]);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      
      // Track failed search
      analytics.searchSO(soNumber, false, Math.round(performance.now() - startTime));
      analytics.apiError('fetchProgramData', err.statusCode || 'unknown', err.statusCode);
      
      // Don't show toast here since we have a nice error card
      setError(err.message || 'Error fetching program data');
      setLoading(false);
      setLoadingSessions(false);
      setLoadingAttendees(false);
    }
  }, []); // Empty dependency array since it only uses props/state from closure


  const handleSearch = (overrideSO = null) => {
    // Use provided SO number (from dropdown selection) or current search input
    const soToSearch = overrideSO || searchSO;
    
    // Validate input - must be a non-empty string
    if (!soToSearch || typeof soToSearch !== 'string' || !soToSearch.trim()) {
      setError('Please enter an SO number');
      return;
    }
    
    // Navigate to the appropriate URL based on context
    if (salesMode) {
      // Stay within sales dashboard context - use query params instead of route params
      navigate(`/sales/scheduled-offerings/search?so=${soToSearch.trim()}`);
    } else if (adminMode) {
      // Stay within admin dashboard context - use query params instead of route params
      navigate(`/admin/scheduled-offerings/search?so=${soToSearch.trim()}`);
    } else {
      // Navigate to the SO-specific URL for main app
      navigate(`/${soToSearch.trim()}`);
    }
  };

  // Handle authentication errors by setting read-only mode
  const handleAuthError = (error) => {
    // If we get an authentication or permission error, switch to read-only mode
    if (error.statusCode === 401 || error.statusCode === 403) {
      setReadOnlyMode(true);
      toast({
        title: "Authentication Error",
        description: "Switching to read-only mode due to insufficient permissions.",
        variant: "destructive",
      });
      console.error("Authentication error, switching to read-only mode:", error);
      return true;
    }
    return false;
  };

  const handleAttendanceToggle = async (attendee, sessionId) => {
    // Prevent modifications in read-only mode
    if (readOnlyMode) {
      toast({
        title: "Read-Only Mode",
        description: "Cannot modify attendance in read-only mode.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Set loading state for this specific checkbox only
      const loadingKey = `${attendee.attendeeId}-${sessionId}`;
      setLoadingAttendanceMap(prev => ({
        ...prev,
        [loadingKey]: true
      }));
      
      const attendanceRecordId = attendee.attendanceRecords?.[sessionId];
      
      // Get current status, default to false if not set
      const currentStatus = attendee.sessions?.[sessionId] || false;
      
      // For a new record we always set it to present (true)
      // For existing records, toggle between present and not present
      const newStatus = !attendanceRecordId ? true : !currentStatus;
      
      // Track attendance toggle
      const sessionInfo = programData.sessions?.find(s => s.sessionId === sessionId);
      const sessionType = sessionInfo ? 'scheduled' : 'unknown';
      analytics.toggleAttendance(newStatus, sessionType);

      // If no record exists, create one first
      if (!attendanceRecordId) {
        try {
          // Create a new attendance record (which will be marked as present)
          const newRecord = await createAttendanceRecord(attendee.attendeeId, sessionId);
          
          if (newRecord && newRecord.id) {
            // Update the local state to include the new record ID and mark as PRESENT
            if (programData && programData.attendees) {
              const updatedWithRecordId = programData.attendees.map(a => {
                if (a.attendeeId === attendee.attendeeId) {
                  const updatedAttendee = {
                    ...a,
                    attendanceRecords: {
                      ...a.attendanceRecords,
                      [sessionId]: newRecord?.id
                    },
                    sessions: {
                      ...a.sessions,
                      [sessionId]: true  // <-- Very important to set this to true
                    }
                  };
                  return updatedAttendee;
                }
                return a;
              });
              
              setProgramData(prev => ({
                ...prev,
                attendees: updatedWithRecordId
              }));
              
              // Also update the attendees array to keep both in sync
              setAttendees(
                attendees.map(a => {
                  if (a.attendeeId === attendee.attendeeId) {
                    return {
                      ...a,
                      attendanceRecords: {
                        ...a.attendanceRecords,
                        [sessionId]: newRecord?.id
                      },
                      sessions: {
                        ...a.sessions,
                        [sessionId]: true  // <-- Very important to set this to true
                      }
                    };
                  }
                  return a;
                })
              );
            } else {
              // Update the standalone attendees array
              const updatedWithRecordId = attendees.map(a => {
                if (a.attendeeId === attendee.attendeeId) {
                  return {
                    ...a,
                    attendanceRecords: {
                      ...a.attendanceRecords,
                      [sessionId]: newRecord?.id
                    },
                    sessions: {
                      ...a.sessions,
                      [sessionId]: true  // <-- Very important to set this to true
                    }
                  };
                }
                return a;
              });
              setAttendees(updatedWithRecordId);
            }
          } else {
            throw new Error('Failed to create attendance record - no ID returned');
          }
        } catch (err) {
          console.error('Error creating attendance record:', err);
          toast({
            title: "Error",
            description: `Failed to create attendance record: ${err.message || String(err)}`,
            variant: "destructive",
          });
          // Revert UI change
          revertUIChange(attendee, sessionId, currentStatus);
          // Clear loading state for this specific checkbox
          setLoadingAttendanceMap(prev => {
            const newMap = {...prev};
            delete newMap[loadingKey];
            return newMap;
          });
          return;
        }
      } else {
        // Update existing record using the imported function
        try {
          await updateAttendanceStatus(attendanceRecordId, newStatus);
          // Update UI for existing records
          if (programData && programData.attendees) {
            const updatedAttendees = programData.attendees.map(a => {
              if (a.attendeeId === attendee.attendeeId) {
                return {
                  ...a,
                  sessions: {
                    ...a.sessions,
                    [sessionId]: newStatus
                  }
                };
              }
              return a;
            });
            
            setProgramData(prev => ({
              ...prev,
              attendees: updatedAttendees
            }));
            
            // Keep attendees array in sync too
            setAttendees(
              attendees.map(a => {
                if (a.attendeeId === attendee.attendeeId) {
                  return {
                    ...a,
                    sessions: {
                      ...a.sessions,
                      [sessionId]: newStatus
                    }
                  };
                }
                return a;
              })
            );
          } else {
            // If programData structure is different, update attendees array directly
            const updatedAttendees = attendees.map(a => {
              if (a.attendeeId === attendee.attendeeId) {
                return {
                  ...a,
                  sessions: {
                    ...a.sessions,
                    [sessionId]: newStatus
                  }
                };
              }
              return a;
            });
            setAttendees(updatedAttendees);
            // Update the attendee counts in response data
            updateAttendeeCountsInResponses(updatedAttendees);
          }
        } catch (error) {
          console.error('Error updating attendance:', error);
          
          // Check for auth errors and set read-only mode if needed
          if (!handleAuthError(error)) {
            // Only show this error toast if it's not an auth error
            toast({
              title: "Error",
              description: `Failed to update attendance: ${error.message || String(error)}`,
              variant: "destructive",
            });
          }
          
          // Revert UI change
          revertUIChange(attendee, sessionId, currentStatus);
        }
      }
    } catch (error) {
      console.error('Error handling attendance:', error);
      toast({
        title: "Error",
        description: `Failed to update attendance: ${error.message || String(error)}`,
        variant: "destructive",
      });
    } finally {
      // Clear loading state for just this specific checkbox
      const loadingKey = `${attendee.attendeeId}-${sessionId}`;
      setLoadingAttendanceMap(prev => {
        const newMap = {...prev};
        delete newMap[loadingKey];
        return newMap;
      });
    }
  };
  
  // Helper function to revert UI state on error
  const revertUIChange = (attendee, sessionId, originalStatus) => {
    if (programData && programData.attendees) {
      const revertedAttendees = programData.attendees.map(a => {
        if (a.attendeeId === attendee.attendeeId) {
          return {
            ...a,
            sessions: {
              ...a.sessions,
              [sessionId]: originalStatus
            }
          };
        }
        return a;
      });
      
      setProgramData(prev => ({
        ...prev,
        attendees: revertedAttendees
      }));
    } else {
      // If programData structure is different, update attendees array directly
      const updatedAttendees = attendees.map(a => {
        if (a.attendeeId === attendee.attendeeId) {
          return {
            ...a,
            sessions: {
              ...a.sessions,
              [sessionId]: originalStatus
            }
          };
        }
        return a;
      });
      setAttendees(updatedAttendees);
    }
  };


  // Handle opening notes modal
  const handleNotesClick = async (attendee, session) => {
    const attendanceRecordId = attendee.attendanceRecords?.[session.sessionId];
    const currentNotes = attendee.sessionNotes?.[session.sessionId] || '';
    
    let finalAttendanceRecordId = attendanceRecordId;
    
    // If no attendance record exists, create one
    if (!attendanceRecordId) {
      if (readOnlyMode) {
        toast({
          title: "Read-Only Mode",
          description: "Cannot create attendance record in read-only mode.",
          variant: "destructive",
        });
        return;
      }
      
      try {
        const newRecord = await createAttendanceRecord(attendee.attendeeId, session.sessionId, false);
        
        if (newRecord && newRecord.id) {
          finalAttendanceRecordId = newRecord?.id;
          
          // Update the local state to include the new record ID
          const updatedAttendees = attendees.map(a => {
            if (a.attendeeId === attendee.attendeeId) {
              return {
                ...a,
                attendanceRecords: {
                  ...a.attendanceRecords,
                  [session.sessionId]: newRecord?.id
                },
                sessions: {
                  ...a.sessions,
                  [session.sessionId]: newRecord?.present
                },
                sessionNotes: {
                  ...a.sessionNotes,
                  [session.sessionId]: ''
                }
              };
            }
            return a;
          });
          setAttendees(updatedAttendees);
          
          // Also update programData if it exists
          if (programData && programData.attendees) {
            setProgramData(prev => ({
              ...prev,
              attendees: prev.attendees.map(a => {
                if (a.attendeeId === attendee.attendeeId) {
                  return {
                    ...a,
                    attendanceRecords: {
                      ...a.attendanceRecords,
                      [session.sessionId]: newRecord?.id
                    },
                    sessions: {
                      ...a.sessions,
                      [session.sessionId]: newRecord?.present
                    },
                    sessionNotes: {
                      ...a.sessionNotes,
                      [session.sessionId]: ''
                    }
                  };
                }
                return a;
              })
            }));
          }
        } else {
          throw new Error('Failed to create attendance record - no ID returned');
        }
      } catch (err) {
        console.error('Error creating attendance record for notes:', err);
        toast({
          title: "Error",
          description: `Failed to create attendance record: ${err.message}`,
          variant: "destructive",
        });
        return;
      }
    }
    
    setSelectedNotesData({
      attendee,
      session,
      attendanceRecordId: finalAttendanceRecordId,
      initialNotes: currentNotes
    });
    setNotesModalOpen(true);
  };

  // Check for potential duplicate attendees
  const checkForDuplicates = (firstName, lastName) => {
    const inputFirst = firstName.trim().toLowerCase();
    const inputLast = lastName.trim().toLowerCase();
    
    const matches = attendees.filter(attendee => {
      const existingFirst = attendee.firstName.toLowerCase();
      const existingLast = attendee.lastName.toLowerCase();
      
      // Exact match (case insensitive)
      if (existingFirst === inputFirst && existingLast === inputLast) {
        return true;
      }
      
      // Swapped first/last names
      if (existingFirst === inputLast && existingLast === inputFirst) {
        return true;
      }
      
      // Similar names (using simple similarity check)
      const firstSimilar = calculateSimilarity(existingFirst, inputFirst) > 0.8;
      const lastSimilar = calculateSimilarity(existingLast, inputLast) > 0.8;
      
      // Check if either first name is similar to existing last name and vice versa
      const firstToLastSimilar = calculateSimilarity(existingFirst, inputLast) > 0.8;
      const lastToFirstSimilar = calculateSimilarity(existingLast, inputFirst) > 0.8;
      
      return (firstSimilar && lastSimilar) || (firstToLastSimilar && lastToFirstSimilar);
    });
    
    return matches;
  };
  
  // Simple similarity calculation using Levenshtein distance
  const calculateSimilarity = (str1, str2) => {
    if (!str1 || !str2) return 0;
    
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const distance = levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  };
  
  // Levenshtein distance algorithm
  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  };

  // Handle adding attendee with duplicate check
  const handleAddAttendeeWithCheck = async () => {
    if (!newAttendee.firstName.trim() || !newAttendee.lastName.trim()) {
      toast({
        title: "Error",
        description: "Please enter both first and last name",
        variant: "destructive",
      });
      return;
    }
    
    // Validate client selection for conditional programs
    const validation = validateClientSelection(
      conditionalClient.programType,
      conditionalClient.selectedClient,
      conditionalClient.participatingClients
    );
    
    if (!validation.isValid) {
      toast({
        title: "Error",
        description: validation.error,
        variant: "destructive",
      });
      return;
    }
    
    // Check for potential duplicates
    const duplicates = checkForDuplicates(newAttendee.firstName, newAttendee.lastName);
    
    if (duplicates.length > 0) {
      setPotentialDuplicates(duplicates);
      setDuplicateCheckOpen(true);
      return;
    }
    
    // No duplicates found, proceed with adding
    await performAddAttendee();
  };
  
  // Actually perform the add attendee action
  const performAddAttendee = async () => {
    setAddingAttendee(true);
    try {
      const attendeeData = {
        ...newAttendee,
        clientId: conditionalClient.selectedClient?.id || newAttendee.clientId
      };
      const addedAttendee = await addAttendee(programData.soid, attendeeData);
      const updatedAttendees = [...attendees, addedAttendee];
      setAttendees(updatedAttendees);
      setNewAttendee({ firstName: '', lastName: '', email: '', clientId: getDefaultClientId() });
      
      // Clear conditional client selection
      conditionalClient.clearSelection();
      
      // Update attendee counts in response data
      updateAttendeeCountsInResponses(updatedAttendees);
      
      // Track successful attendee addition
      analytics.addAttendee(true);
      
      toast({
        title: "Success",
        description: `${addedAttendee.firstName} ${addedAttendee.lastName} added successfully`,
        variant: "default",
      });

      // Close the add attendee dialog using state
      setAddDialogOpen(false);
    } catch (err) {
      console.error('Error adding attendee:', err);
      
      // Track failed attendee addition
      analytics.addAttendee(false);
      
      // Check for auth errors and set read-only mode if needed
      if (!handleAuthError(err)) {
        // Only show this error toast if it's not an auth error
        toast({
          title: "Error",
          description: `Failed to add attendee: ${err.message}`,
          variant: "destructive",
        });
        setError('Failed to add attendee. Please try again.');
      }
    } finally {
      setAddingAttendee(false);
    }
  };

  // Handle SO notes save
  const handleSaveSONotes = async () => {
    if (readOnlyMode) {
      toast({
        title: "Read-Only Mode",
        description: "Cannot modify notes in read-only mode.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSavingNotes(true);
      await updateSONotes(programData.soid, soNotes);
      toast({
        title: "Success",
        description: "Program notes saved successfully",
        variant: "default",
      });
    } catch (err) {
      console.error('Error saving SO notes:', err);
      
      // Check for auth errors and set read-only mode if needed
      if (!handleAuthError(err)) {
        toast({
          title: "Error",
          description: `Failed to save notes: ${err.message}`,
          variant: "destructive",
        });
      }
    } finally {
      setSavingNotes(false);
    }
  };

  // Helper function to update attendee counts in response data
  const updateAttendeeCountsInResponses = (attendeesList) => {
    setResponseCounts(prevCounts => {
      const updatedCounts = {...prevCounts};
      
      // For each session, count total attendees (not just those marked present)
      // All attendees are eligible to take the survey
      Object.keys(updatedCounts).forEach(sessionId => {
        updatedCounts[sessionId] = {
          ...updatedCounts[sessionId],
          attendees: attendeesList.length // Total number of attendees for the program
        };
      });
      
      return updatedCounts;
    });
  };
  
  // Handle notes update
  const handleNotesUpdated = (attendanceRecordId, newNotes) => {
    // Update the local state with the new notes
    setAttendees(attendees.map(a => {
      // Find the attendee and session that matches this attendance record
      const sessionId = Object.keys(a.attendanceRecords || {}).find(
        sid => a.attendanceRecords[sid] === attendanceRecordId
      );
      
      if (sessionId) {
        return {
          ...a,
          sessionNotes: {
            ...a.sessionNotes,
            [sessionId]: newNotes
          }
        };
      }
      return a;
    }));
    
    // Also update programData if it exists
    if (programData && programData.attendees) {
      setProgramData(prev => ({
        ...prev,
        attendees: prev.attendees.map(a => {
          const sessionId = Object.keys(a.attendanceRecords || {}).find(
            sid => a.attendanceRecords[sid] === attendanceRecordId
          );
          
          if (sessionId) {
            return {
              ...a,
              sessionNotes: {
                ...a.sessionNotes,
                [sessionId]: newNotes
              }
            };
          }
          return a;
        })
      }));
    }
  };

  const exportToExcel = () => {
    if (!programData || !programData.sessions || programData.sessions.length === 0) {
      toast({
        title: "Error",
        description: 'No session data available for export',
        variant: "destructive",
      });
      setError('No session data available for export');
      return;
    }

    // Track CSV export
    analytics.exportCSV(attendees.length, programData.sessions.length);

    setExporting(true);
    
    try {
      // Create dynamic headers based on available sessions - use forCSV=true to remove commas
      const sessionHeaders = programData.sessions.map(session => 
        `Session ${session.sessionNo} (${formatDate(session.start, true)})`
      );
      
      // Add session notes headers
      const sessionNotesHeaders = programData.sessions.map(session => 
        `Session ${session.sessionNo} Notes`
      );

      // Conditionally add Company column for OPEN-PROGRAM SOs
      const baseHeaders = ['Attendee ID', 'First Name', 'Last Name', 'Email'];
      const companyHeader = shouldShowClientColumn() ? ['Company'] : [];
      const headers = [...baseHeaders, ...companyHeader, ...sessionHeaders, ...sessionNotesHeaders, 'General Notes'];
      
      // Helper function to escape CSV fields properly
      const escapeCSV = (field) => {
        if (field === null || field === undefined) return '';
        const stringField = String(field);
        
        // If the field contains commas, quotes, or newlines, wrap it in quotes and escape any quotes
        if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };
      
      // Sort attendees by first name for CSV export
      const sortedAttendeesForCSV = [...attendees].sort((a, b) => 
        a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase())
      );
      
      // Create CSV content with proper escaping
      const csvContent = [
        headers.map(escapeCSV).join(','),
        ...sortedAttendeesForCSV.map(attendee => {
          const sessionValues = programData.sessions.map(session => 
            attendee.sessions[session.sessionId] ? 'Present' : ''
          );
          
          const sessionNotesValues = programData.sessions.map(session => 
            attendee.sessionNotes?.[session.sessionId] || ''
          );
          
          // Conditionally add company data for OPEN-PROGRAM SOs
          const baseData = [
            escapeCSV(attendee.attendeeId),
            escapeCSV(attendee.firstName),
            escapeCSV(attendee.lastName),
            escapeCSV(attendee.email || '')
          ];
          const companyData = shouldShowClientColumn() ? [escapeCSV(attendee.clientName || '')] : [];
          
          return [
            ...baseData,
            ...companyData,
            ...sessionValues.map(escapeCSV),
            ...sessionNotesValues.map(escapeCSV),
            escapeCSV(attendee.notes || '')
          ].join(',');
        })
      ].join('\n');

      // Create and trigger download - use forCSV=true to avoid commas in filename
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      // Create filename with program details
      const sanitizeFilename = (value) => {
        if (!value) return '';
        const str = Array.isArray(value) ? value.join(' ') : String(value);
        return str.replace(/[^a-zA-Z0-9-_\s]/g, '').replace(/\s+/g, '_');
      };
      const filename = [
        sanitizeFilename(programData.programName) || 'Program',
        `SO_${programData.so}`,
        sanitizeFilename(programData.clientName) || 'Client',
        sanitizeFilename(programData.plName) || 'PL',
        formatDate(new Date(), true)
      ].join('_');
      link.download = `attendance_${filename}.csv`;
      link.click();
      
      toast({
        title: "Success",
        description: 'Attendance data exported successfully',
        variant: "default",
      });
    } catch (error) {
      console.error('Error exporting data:', error);
      toast({
        title: "Error",
        description: 'Failed to export attendance data',
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  const exportAttendanceSheet = () => {
    if (!attendees || attendees.length === 0) {
      toast({
        title: "Error",
        description: 'No attendee data available for attendance sheet',
        variant: "destructive",
      });
      return;
    }
    
    setExporting(true);
    
    try {
      // Simple headers for attendance sheet
      const headers = ['Full Name', 'Signature'];
      
      // Helper function to escape CSV fields properly
      const escapeCSV = (field) => {
        if (field === null || field === undefined) return '';
        const stringField = String(field);
        
        // If the field contains commas, quotes, or newlines, wrap it in quotes and escape any quotes
        if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };
      
      // Sort attendees by first name alphabetically
      const sortedAttendees = [...attendees].sort((a, b) => 
        a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase())
      );
      
      // Create CSV content with proper escaping
      const csvContent = [
        headers.join(','),
        ...sortedAttendees.map(attendee => {
          const fullName = `${attendee.firstName} ${attendee.lastName}`;
          return [
            escapeCSV(fullName),
            '' // Empty signature column
          ].join(',');
        })
      ].join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `SO-${programData.so}-attendance-sheet.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Success",
        description: `Attendance sheet downloaded for ${sortedAttendees.length} attendees`,
      });
      
    } catch (error) {
      console.error('Attendance sheet export error:', error);
      toast({
        title: "Error",
        description: 'Failed to export attendance sheet',
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6">
      {/* Search Card */}
      {/* Only show warning if read-only mode is due to permission issues, not intentional (sales dashboard) */}
      {readOnlyMode && !readOnly && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
          <div className="flex items-center space-x-2">
            <AlertOctagon className="h-5 w-5 text-orange-500" />
            <p className="text-orange-800 font-medium">Read-Only Mode</p>
          </div>
          <p className="mt-1 text-orange-700 text-sm">
            You are viewing in read-only mode due to permission issues. Some features like attendance tracking and adding attendees are disabled.
          </p>
        </div>
      )}
      <Card className="bg-card shadow-sm hover:shadow-md transition-shadow">
        <CardHeader className="border-b px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg sm:text-xl">SO Lookup</CardTitle>
            {!hideNavButtons && (
              <div className="flex items-center space-x-2">
                {/* Sales Dashboard button - always visible, login required on page */}
                <Button variant="outline" size="sm" asChild className="flex items-center space-x-2">
                  <Link to="/sales/dashboard" aria-label="Open Sales Dashboard">
                    <BarChart3 className="h-4 w-4" />
                    <span className="hidden sm:inline">Sales Dashboard</span>
                  </Link>
                </Button>
                {/* Admin button - hidden in production via feature flag */}
                {(import.meta.env.VITE_ENABLE_ADMIN_FEATURES === 'true' || import.meta.env.DEV) && (
                  <Button variant="outline" size="sm" asChild className="flex items-center space-x-2">
                    <Link to="/admin">
                      <Settings className="h-4 w-4" />
                      <span className="hidden sm:inline">Admin</span>
                    </Link>
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <SOSearchDropdown
            value={searchSO}
            onChange={setSearchSO}
            onSubmit={handleSearch}
            loading={loading}
            placeholder="Enter SO number"
          />
        </CardContent>
      </Card>

      {/* Show skeleton loader while loading */}
      {loading && <ProgramSkeleton />}

      {/* Show helpful message when no program is found */}
      {!loading && !programData && error && (
        <Card className="bg-card shadow-sm">
          <CardContent className="p-6 sm:p-8 text-center">
            <div className="mx-auto w-16 h-16 mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertOctagon className="h-8 w-8 text-destructive" />
            </div>
            {isAirtableDown ? (
              <>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">Airtable Service Disruption</h3>
                <p className="text-muted-foreground mb-4 text-sm sm:text-base">
                  Airtable is currently experiencing issues, so we can't load program data right now. This is not an issue with SO Lookup.
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Check <a href="https://status.airtable.com" target="_blank" rel="noopener noreferrer" className="underline">Airtable Status</a> for updates, or try again in a few minutes.
                </p>
              </>
            ) : (
              <>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">Scheduled Offering Not Found</h3>
                <p className="text-muted-foreground mb-4 text-sm sm:text-base">
                  {error.includes('not found')
                    ? `We couldn't find a Scheduled Offering with SO number "${searchSO}". Please check the number and try again.`
                    : error}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  If you believe this is an error, please contact Tara at <a href="mailto:tara@avenuecareers.com" className="underline">tara@avenuecareers.com</a> or Kyla at <a href="mailto:kyla@avenuecareers.com" className="underline">kyla@avenuecareers.com</a>.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Program Details Card */}
      {!loading && programData && (
        <>
          <Card className="bg-card shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="border-b px-4 py-4 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg sm:text-xl font-bold break-words">
                    {programData.type && <span className="font-medium text-muted-foreground mr-2">{programData.type}:</span>}
                    {programData.programName}
                  </CardTitle>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mt-1">
                    <p className="text-sm text-muted-foreground">SO: {programData.so}</p>
                    {programData.status && (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-fit ${
                        programData.status === 'Active' 
                          ? 'bg-green-100 text-green-800'
                          : programData.status === 'Completed'
                          ? 'bg-gray-100 text-gray-800'
                          : programData.status === 'Cancelled'
                          ? 'bg-red-100 text-red-800'
                          : programData.status === 'Pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {programData.status}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCopyPageLink}
                  className="min-h-[44px] px-4 whitespace-nowrap w-full sm:w-auto"
                >
                  {pageCopied ? (
                    <>
                      <Check className="h-4 w-4 mr-2 text-green-500" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      <span>Copy Link</span>
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Client and Program Leader Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <Card className="bg-accent/50 border border-accent shadow-none">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <h3 className="text-sm font-medium text-muted-foreground">Client</h3>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 pt-0">
                    <p className="font-semibold text-foreground">{programData.clientName}</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-accent/50 border border-accent shadow-none">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <h3 className="text-sm font-medium text-muted-foreground">Program Leader</h3>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 pt-0">
                    <p className="font-semibold text-foreground">
                      {programData.plName === 'Helene Bellerose' ? 'Hélène Bellerose' : programData.plName}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Delivery Method */}
              <div className="max-w-md">
                <Card className="bg-accent/50 border border-accent shadow-none">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <h3 className="text-sm font-medium text-muted-foreground">
                      {(isVirtualType(programData.type) && !isPilotInPersonType(programData.type) && !isInPersonType(programData.type)) || (programData.virtualPlatform && isVirtualType(programData.type)) ? 'Virtual Platform' : 'Delivery Type'}
                    </h3>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 pt-0">
                    <div className="flex items-start gap-3">
                      {(isVirtualType(programData.type) && !isPilotInPersonType(programData.type) && !isInPersonType(programData.type)) || (programData.virtualPlatform && isVirtualType(programData.type)) ? (
                        <>
                          <Video className="h-5 w-5 text-primary mt-0.5" />
                          <p className="font-semibold text-foreground">
                            {programData.virtualPlatform || 'Virtual'}
                          </p>
                        </>
                      ) : (
                        <>
                          <Building2 className="h-5 w-5 text-primary mt-0.5" />
                          <p className="font-semibold text-foreground">In Person</p>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

            </CardContent>
          </Card>

          {/* Sessions Card */}
          <Card className="bg-card shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="border-b px-4 py-4 sm:px-6 flex flex-row items-center justify-between">
              <CardTitle className="text-lg sm:text-xl">Sessions</CardTitle>
              {!loadingSessions && !showInstructions && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowInstructions(true)}
                  className="text-xs sm:text-sm text-blue-600 hover:text-blue-800"
                >
                  <HelpCircle className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Survey Instructions</span>
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {/* Instructions */}
              {!loadingSessions && showInstructions && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex justify-between items-start">
                    <div className="text-xs sm:text-sm text-blue-800 space-y-2 flex-1">
                      <div className="flex flex-wrap items-start gap-x-2">
                        <strong className="whitespace-nowrap">Survey Links:</strong> 
                        <span>Use <ExternalLink className="h-3 w-3 inline mx-1" /> to open survey or <Copy className="h-3 w-3 inline mx-1" /> to copy link.</span>
                      </div>
                      <div className="flex flex-wrap items-start gap-x-2">
                        <strong className="whitespace-nowrap">Response Tracking:</strong>
                        <span>Numbers show responses/total attendees (e.g., 28/45 means 28 responses from 45 attendees).</span>
                      </div>
                      <div className="flex flex-wrap items-start gap-x-2">
                        <strong className="whitespace-nowrap">In-Person Sessions:</strong>
                        <span>QR codes available for easy survey access during live sessions.</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowInstructions(false)}
                      className="ml-2 p-1 h-6 w-6 flex-shrink-0"
                      title="Hide instructions"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              {loadingSessions ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="default" className="mr-2" />
                  <span className="text-muted-foreground">Loading sessions...</span>
                </div>
              ) : (
              <>
                {/* Mobile Card Layout */}
                <div className="md:hidden space-y-4">
                  {programData.sessions?.map((session, index) => {
                    // Determine if this session is virtual using the utility function
                    const isVirtualSession = isSessionVirtual(programData.type);
                    const isInPersonSession = !isVirtualSession;
                    
                    // Use Survey Link TRUTH as the single source of truth
                    const surveyUrl = session.surveyLink?.trim() || null;

                    return (
                      <Card key={session.sessionId} className="bg-accent/30 border border-accent">
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            {/* Session Header */}
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-base">Session {session.sessionNo}</h4>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                session.sessionStatus === 'Completed' 
                                  ? 'bg-green-100 text-green-800'
                                  : session.sessionStatus === 'Scheduled'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {session.sessionStatus}
                              </span>
                            </div>

                            {/* Date & Time */}
                            <div className="flex items-start gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                              <div className="text-sm">
                                <div className="font-medium">
                                  {new Intl.DateTimeFormat('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  }).format(new Date(session.start))}
                                </div>
                                <div className="text-muted-foreground">
                                  {new Intl.DateTimeFormat('en-US', {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    timeZoneName: 'short'
                                  }).format(new Date(session.start))}
                                </div>
                              </div>
                            </div>

                            {/* Meeting Info */}
                            <div className="flex items-center gap-2">
                              {isVirtualSession ? (
                                session.meetingLink ? (
                                  <a
                                    href={session.meetingLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:underline flex items-center gap-2 min-h-[44px] py-2"
                                  >
                                    <Video className="h-4 w-4" />
                                    <span className="text-sm">{programData.virtualPlatform || 'Virtual Meeting'}</span>
                                  </a>
                                ) : (
                                  <div className="flex items-center gap-2 text-muted-foreground">
                                    <Video className="h-4 w-4" />
                                    <span className="text-sm">{programData.virtualPlatform || 'Virtual'} - No Link</span>
                                  </div>
                                )
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Building2 className="h-4 w-4" />
                                  <span className="text-sm">In Person</span>
                                </div>
                              )}
                            </div>

                            {/* Survey Actions */}
                            {shouldShowSurvey(programData.programName, index, programData.sessions.length, surveyUrl) && (
                              <div className="pt-2 border-t border-accent">
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                  <span className="text-sm font-medium text-muted-foreground">
                                    Survey:
                                    {loadingResponseCounts[session.sessionId] ? (
                                      <span className="ml-2 inline-flex items-center text-xs text-muted-foreground">
                                        <Spinner size="sm" className="mr-1" />
                                        <span>Loading...</span>
                                      </span>
                                    ) : (
                                      responseCounts[session.sessionId] && responseCounts[session.sessionId].responses > 0 && programData.so === '551' && (
                                        <Link
                                          to={`/sos/${programData.so}/sessions/${session.sessionId}/report`}
                                          className="ml-2 text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded hover:bg-green-200 transition-colors"
                                          title="View survey report"
                                        >
                                          {responseCounts[session.sessionId].responses}/{responseCounts[session.sessionId].attendees || 0} responses
                                        </Link>
                                      )
                                    )}
                                  </span>
                                  <div className="flex items-center gap-1 sm:gap-2">
                                    {/* Open Survey Button */}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      asChild
                                      className="min-h-[44px] px-2 sm:px-3 flex-1 sm:flex-initial"
                                      title={responseCounts[session.sessionId] ? `Open Survey (${responseCounts[session.sessionId].responses}/${responseCounts[session.sessionId].attendees || 0} responses)` : 'Open Survey'}
                                    >
                                      <a
                                        href={surveyUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2"
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                        <span className="text-xs">Open</span>
                                      </a>
                                    </Button>
                                    
                                    {/* Copy Survey Link Button */}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleSessionSurveyCopy(session.sessionId, surveyUrl)}
                                      className="min-h-[44px] px-2 sm:px-3 flex-1 sm:flex-initial"
                                    >
                                      <div className="flex items-center gap-2">
                                        {copiedSessionSurveys[session.sessionId] ? (
                                          <Check className="h-3 w-3 text-green-600" />
                                        ) : (
                                          <Copy className="h-3 w-3" />
                                        )}
                                        <span className="text-xs">
                                          {copiedSessionSurveys[session.sessionId] ? 'Copied' : 'Copy'}
                                        </span>
                                      </div>
                                    </Button>

                                    {/* QR Code Button - Only for in-person sessions */}
                                    {isInPersonSession && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleShowQRCode(surveyUrl)}
                                        className="min-h-[44px] px-2 sm:px-3 flex-1 sm:flex-initial"
                                      >
                                        <div className="flex items-center gap-2">
                                          <QrCode className="h-3 w-3" />
                                          <span className="text-xs">QR</span>
                                        </div>
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  
                  {(!programData.sessions || programData.sessions.length === 0) && (
                    <div className="text-center py-8 text-muted-foreground">
                      No sessions found for this scheduled offering.
                    </div>
                  )}
                </div>

                {/* Desktop Table Layout */}
                <div className="hidden md:block overflow-x-auto -mx-4 sm:-mx-6">
                  <div className="inline-block min-w-full px-4 sm:px-6 py-2 align-middle">
                    <Table className="min-w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Session</TableHead>
                        <TableHead>Start Date & Time</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Meeting</TableHead>
                        <TableHead className="text-center">Survey</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {programData.sessions?.map((session, index) => (
                        <TableRow key={session.sessionId}>
                          <TableCell className="font-medium">Session {session.sessionNo}</TableCell>
                          <TableCell>
                            {new Intl.DateTimeFormat('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              timeZoneName: 'short'
                            }).format(new Date(session.start))}
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              session.sessionStatus === 'Completed' 
                                ? 'bg-green-100 text-green-800'
                                : session.sessionStatus === 'Scheduled'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {session.sessionStatus}
                            </span>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              // Determine if this session is virtual using the utility function
                              const isVirtualSession = isSessionVirtual(programData.type);
                              
                              if (isVirtualSession) {
                                if (session.meetingLink) {
                                  return (
                                    <a
                                      href={session.meetingLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-500 hover:underline flex items-center gap-2"
                                    >
                                      <Video className="h-4 w-4" />
                                      {programData.virtualPlatform || 'Virtual Meeting'}
                                    </a>
                                  );
                                } else {
                                  return (
                                    <span className="flex items-center gap-2 text-muted-foreground">
                                      <Video className="h-4 w-4" />
                                      {programData.virtualPlatform || 'Virtual'} - No Link
                                    </span>
                                  );
                                }
                              } else {
                                return (
                                  <span className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4" />
                                    In Person
                                  </span>
                                );
                              }
                            })()}
                          </TableCell>
                          <TableCell className="text-center">
                            {(() => {
                              // Use Survey Link TRUTH as the single source of truth
                              const surveyUrl = session.surveyLink?.trim() || null;
                              
                              if (shouldShowSurvey(programData.programName, index, programData.sessions.length, surveyUrl)) {
                                // Determine if this session is in-person (not virtual) using the utility function
                                const isVirtualSession = isSessionVirtual(programData.type);
                                const isInPersonSession = !isVirtualSession;
                                
                                const responseData = responseCounts[session.sessionId];
                                
                                return (
                                  <div className="flex items-center justify-center gap-1">
                                    {/* Response count badge */}
                                    {loadingResponseCounts[session.sessionId] ? (
                                      <Spinner size="sm" className="text-muted-foreground" />
                                    ) : (
                                      responseData && responseData.responses > 0 && programData.so === '551' && (
                                        <Link
                                          to={`/sos/${programData.so}/sessions/${session.sessionId}/report`}
                                          className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded min-w-[44px] text-center hover:bg-green-200 transition-colors inline-block"
                                          title="View survey report"
                                        >
                                          {responseData.responses}/{responseData.attendees || 0}
                                        </Link>
                                      )
                                    )}
                                    
                                    {/* Go to Survey Button */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      asChild
                                      className="min-h-[44px] min-w-[44px] p-0"
                                      title={`Open Survey${responseData ? ` (${responseData.responses}/${responseData.attendees || 0} responses)` : ''}`}
                                    >
                                      <a
                                        href={surveyUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    </Button>
                                    
                                    {/* Copy Survey Link Button */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleSessionSurveyCopy(session.sessionId, surveyUrl)}
                                      className="min-h-[44px] min-w-[44px] p-0"
                                      title="Copy Survey Link"
                                    >
                                      {copiedSessionSurveys[session.sessionId] ? (
                                        <Check className="h-3 w-3 text-green-600" />
                                      ) : (
                                        <Copy className="h-3 w-3" />
                                      )}
                                    </Button>

                                    {/* QR Code Button - Only for in-person sessions */}
                                    {isInPersonSession && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleShowQRCode(surveyUrl)}
                                        className="min-h-[44px] min-w-[44px] p-0"
                                        title="Show QR Code"
                                      >
                                        <QrCode className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!programData.sessions || programData.sessions.length === 0) && (
                        <TableRow>
                          <TableCell colSpan="5" className="py-6 text-center text-muted-foreground">
                            No sessions found for this scheduled offering.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    </Table>
                  </div>
                </div>
              </>
              )}
            </CardContent>
          </Card>

          {/* Materials Section */}
          <MaterialsSection
            programData={programData}
            loading={loadingSessions}
            showInstructions={showMaterialsInstructions}
            onToggleInstructions={() => setShowMaterialsInstructions(!showMaterialsInstructions)}
          />

          {/* Attendance Card */}
          <Card className="bg-card shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="border-b px-4 py-4 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
                <CardTitle className="text-lg sm:text-xl">Attendance</CardTitle>
                <div className="flex items-center space-x-2 sm:space-x-2">
                  <Dialog
                    open={addDialogOpen}
                    onOpenChange={(open) => {
                      setAddDialogOpen(open);
                      // Clear form when dialog closes (use getDefaultClientId for consistency)
                      if (!open) {
                        setNewAttendee({ firstName: '', lastName: '', email: '', clientId: getDefaultClientId() });
                      }
                    }}
                  >
                    <DialogTrigger asChild>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={addingAttendee || readOnlyMode}
                        className="min-h-[44px] px-4 whitespace-nowrap text-sm"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        <span>Add Attendee</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent 
                      className="w-[95vw] max-w-[425px] max-h-[85vh] overflow-y-auto"
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                          e.preventDefault();
                          if (newAttendee.firstName.trim() && newAttendee.lastName.trim() && !addingAttendee) {
                            handleAddAttendeeWithCheck();
                          }
                        }
                      }}
                    >
                      <DialogHeader className="pb-2">
                        <DialogTitle className="text-xl">Add New Attendee</DialogTitle>
                        <DialogDescription className="text-muted-foreground">
                          Enter the details of the new attendee to add them to this program.
                          <br />
                          <span className="text-xs text-muted-foreground">⌘+Enter to save</span>
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                              First Name
                            </label>
                            <Input
                              placeholder="First Name"
                              value={newAttendee.firstName}
                              onChange={(e) => setNewAttendee({...newAttendee, firstName: e.target.value})}
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                              Last Name
                            </label>
                            <Input
                              placeholder="Last Name"
                              value={newAttendee.lastName}
                              onChange={(e) => setNewAttendee({...newAttendee, lastName: e.target.value})}
                              className="h-9"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            Email (Optional)
                          </label>
                          <Input
                            type="email"
                            placeholder="email@example.com"
                            value={newAttendee.email}
                            onChange={(e) => setNewAttendee({...newAttendee, email: e.target.value})}
                            className="h-9"
                          />
                        </div>
                        {conditionalClient.showDropdown && (
                          <div className="space-y-2">
                            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                              Company {conditionalClient.programType === 'open' ? '(Required)' : '(Optional)'}
                            </label>
                            {conditionalClient.isLoading ? (
                              <div className="h-9 flex items-center">
                                <Spinner size="sm" className="mr-2" />
                                <span className="text-sm text-muted-foreground">Loading companies...</span>
                              </div>
                            ) : (
                              <select
                                value={newAttendee.clientId}
                                onChange={(e) => {
                                  const selectedId = e.target.value;
                                  const selectedClient = conditionalClient.participatingClients.find(c => c.id === selectedId);
                                  setNewAttendee({...newAttendee, clientId: selectedId});
                                  if (selectedClient) {
                                    conditionalClient.selectClient(selectedClient);
                                  }
                                }}
                                className="w-full h-9 px-3 py-1 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                required={conditionalClient.programType === 'open'}
                              >
                                <option value="">Select a company...</option>
                                {conditionalClient.participatingClients.map(client => (
                                  <option key={client.id} value={client.id}>
                                    {client.name}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <DialogFooter className="gap-2 pt-2">
                        <DialogClose asChild>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setNewAttendee({ firstName: '', lastName: '', email: '', clientId: getDefaultClientId() });
                              conditionalClient.clearSelection();
                            }}
                            disabled={addingAttendee}
                            className="min-h-[44px] px-4 flex-1 sm:flex-none"
                          >
                            Cancel
                          </Button>
                        </DialogClose>
                        <Button
                          variant="secondary"
                          data-add-attendee="true"
                          onClick={handleAddAttendeeWithCheck}
                          disabled={addingAttendee}
                          className="min-h-[44px] px-4 flex-1 sm:flex-none"
                        >
                          {addingAttendee ? (
                            <>
                              <Spinner size="sm" className="mr-2" />
                              <span>Adding...</span>
                            </>
                          ) : (
                            <>
                              <Plus className="h-4 w-4 mr-2" />
                              <span>Add</span>
                            </>
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={exportToExcel}
                    disabled={exporting || readOnlyMode}
                    className="min-h-[44px] px-4 whitespace-nowrap text-sm"
                  >
                    {exporting ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        <span>Exporting...</span>
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        <span>Export</span>
                      </>
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={exportAttendanceSheet}
                    disabled={exporting || readOnlyMode}
                    className="min-h-[44px] px-4 whitespace-nowrap text-sm"
                  >
                    {exporting ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        <span>Exporting...</span>
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-2" />
                        <span>Attendance Sheet</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {loadingAttendees ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="default" className="mr-2" />
                  <span className="text-muted-foreground">Loading attendees...</span>
                </div>
              ) : (
              <>
                {/* Client Filter - Show for all OPEN-PROGRAM SOs to maintain consistency */}
                {shouldShowClientColumn() && (
                  <div className="mb-4 flex items-center gap-2">
                    <label htmlFor="client-filter" className="text-sm font-medium">
                      Filter by Client:
                    </label>
                    <select
                      id="client-filter"
                      value={clientFilter}
                      onChange={(e) => setClientFilter(e.target.value)}
                      className="px-3 py-1 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                      <option value="all">All {uniqueClients.length} Clients ({attendees.length} Learners)</option>
                      {uniqueClients.map(clientName => {
                        const count = attendees.filter(a => a.clientName === clientName).length;
                        return (
                          <option key={clientName} value={clientName}>
                            {clientName} ({count})
                          </option>
                        );
                      })}
                    </select>
                    {clientFilter !== 'all' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setClientFilter('all')}
                        className="h-8 px-2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              <div className="overflow-x-auto -mx-4 sm:-mx-6">
                <div className="inline-block min-w-full px-4 sm:px-6 py-2 align-middle">
                  <Table className="min-w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">
                        <div className="flex items-center cursor-pointer" onClick={() => toggleSort('name')}>
                          Name
                          {sortField === 'name' && (
                            sortDirection === 'asc' ? (
                              <ChevronUp className="ml-1 h-4 w-4" />
                            ) : (
                              <ChevronDown className="ml-1 h-4 w-4" />
                            )
                          )}
                        </div>
                      </TableHead>
                      {shouldShowClientColumn() && (
                        <TableHead className="w-[200px]">
                          <div className="flex items-center cursor-pointer" onClick={() => toggleSort('company')}>
                            Company
                            {sortField === 'company' && (
                              sortDirection === 'asc' ? (
                                <ChevronUp className="ml-1 h-4 w-4" />
                              ) : (
                                <ChevronDown className="ml-1 h-4 w-4" />
                              )
                            )}
                          </div>
                        </TableHead>
                      )}
                      {programData.sessions?.map(session => (
                        <TableHead key={session.sessionId} className="text-center">
                          Session {session.sessionNo}<br/>
                          <span className="text-xs text-muted-foreground">{formatDate(session.start)}</span>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedAttendees.map((attendee) => (
                      <TableRow key={attendee.attendeeId}>
                        <TableCell className="font-medium">
                          {attendee.firstName} {attendee.lastName}
                        </TableCell>
                        {shouldShowClientColumn() && (
                          <TableCell>
                            {attendee.clientName || '-'}
                          </TableCell>
                        )}
                        {programData.sessions?.map(session => (
                          <TableCell key={session.sessionId} className="text-center">
                            <div className="flex items-center justify-center gap-2 p-1">
                              {loadingAttendanceMap[`${attendee.attendeeId}-${session.sessionId}`] ? (
                                <Spinner size="sm" variant={attendee.sessions[session.sessionId] ? "primary" : "secondary"} />
                              ) : (
                                <Checkbox
                                  checked={attendee.sessions[session.sessionId] || false}
                                  onCheckedChange={() => handleAttendanceToggle(attendee, session.sessionId)}
                                  className={`${attendee.sessions[session.sessionId] ? "data-[state=checked]:bg-[#1a3cfa] data-[state=checked]:border-[#1a3cfa]" : ""} h-5 w-5 ${readOnlyMode ? "opacity-60 cursor-not-allowed" : ""}`}
                                  aria-label={`Attendance status for ${attendee.firstName} ${attendee.lastName} for Session ${session.sessionNo}`}
                                  disabled={readOnlyMode}
                                />
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleNotesClick(attendee, session)}
                                className={`h-6 w-6 p-0 ${readOnlyMode ? "opacity-60 cursor-not-allowed" : ""} hover:bg-accent`}
                                disabled={readOnlyMode}
                                aria-label={`Notes for ${attendee.firstName} ${attendee.lastName} for Session ${session.sessionNo}`}
                              >
                                <FileText 
                                  className={`h-4 w-4 ${
                                    attendee.sessionNotes?.[session.sessionId] && attendee.sessionNotes[session.sessionId].trim()
                                      ? 'text-blue-600'
                                      : 'text-muted-foreground'
                                  }`} 
                                />
                              </Button>
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {attendees.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={(programData.sessions?.length || 0) + (shouldShowClientColumn() ? 2 : 1)} className="py-6 text-center text-muted-foreground">
                          No attendees found. Add an attendee using the button above.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                  </Table>
                </div>
              </div>
              </>
              )}
            </CardContent>
          </Card>

          {/* SO Notes Card */}
          <Card className="bg-card shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="border-b px-4 py-4 sm:px-6">
              <CardTitle className="text-lg sm:text-xl">Program Leader Notes</CardTitle>
              <p className="text-sm text-muted-foreground">
                Share feedback about how the program went, any blockers, barriers, or recommendations for the team. You can add notes for each session and revise as needed.
              </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-4">
                <Textarea
                  placeholder="Enter your notes about this program delivery..."
                  value={soNotes}
                  onChange={(e) => setSoNotes(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                      e.preventDefault();
                      if (!savingNotes && !readOnlyMode) {
                        handleSaveSONotes();
                      }
                    }
                  }}
                  rows={4}
                  className="w-full"
                  disabled={readOnlyMode}
                />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">
                    {navigator.platform.toLowerCase().includes('mac') ? '⌘+Enter' : 'Ctrl+Enter'} to save
                  </span>
                  <Button
                    onClick={handleSaveSONotes}
                    disabled={savingNotes || readOnlyMode}
                    className="min-h-[44px] px-6 text-base"
                  >
                    {savingNotes ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      'Save Notes'
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
      
      {/* Duplicate Check Dialog */}
      <Dialog open={duplicateCheckOpen} onOpenChange={setDuplicateCheckOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl">Possible Duplicate Found</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              We found existing attendees that might be the same person you&apos;re trying to add:
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-blue-50 rounded-md border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">You&apos;re trying to add:</h4>
              <p className="text-blue-800">{newAttendee.firstName} {newAttendee.lastName}</p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-foreground">Existing similar attendees:</h4>
              {potentialDuplicates.map((duplicate) => (
                <div key={duplicate.attendeeId} className="p-3 bg-orange-50 rounded-md border border-orange-200">
                  <p className="text-orange-800 font-medium">
                    {duplicate.firstName} {duplicate.lastName}
                  </p>
                  {duplicate.notes && (
                    <p className="text-orange-700 text-sm mt-1">
                      Notes: {duplicate.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter className="gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setDuplicateCheckOpen(false);
                setPotentialDuplicates([]);
              }}
              className="h-9 px-4"
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={async () => {
                setDuplicateCheckOpen(false);
                setPotentialDuplicates([]);
                await performAddAttendee();
              }}
              disabled={addingAttendee}
              className="h-9 px-4"
            >
              {addingAttendee ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  <span>Adding...</span>
                </>
              ) : (
                'Add Anyway'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Session Notes Modal */}
      {selectedNotesData && (
        <SessionNotesModal
          isOpen={notesModalOpen}
          onClose={() => {
            setNotesModalOpen(false);
            setSelectedNotesData(null);
          }}
          attendee={selectedNotesData.attendee}
          session={selectedNotesData.session}
          attendanceRecordId={selectedNotesData.attendanceRecordId}
          initialNotes={selectedNotesData.initialNotes}
          onNotesUpdated={handleNotesUpdated}
          readOnlyMode={readOnlyMode}
        />
      )}

      {/* QR Code Modal */}
      <Dialog open={qrModalOpen} onOpenChange={setQrModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Survey QR Code</DialogTitle>
            <DialogDescription>
              Scan this QR code with your phone to access the survey, or download it for presentations
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-6">
            <QRCodeSVG 
              className="qr-code-svg"
              value={qrSurveyUrl} 
              size={320} 
              bgColor="#ffffff"
              fgColor="#000000"
              level="M"
              marginSize={4}
              title="Survey QR Code"
            />
          </div>
          <div className="flex justify-between items-center">
            <Button 
              variant="outline" 
              onClick={handleDownloadQR}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download QR Code
            </Button>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramManager;